/**
 * Reconnect policy for the live-call sockets (salesguru-workspace
 * docs/REALTIME_ARCHITECTURE.md §10 "Reconnects", the contract every app builds
 * against): while a session is active, a dropped socket retries after 1 s,
 * doubling to a 10 s ceiling, with no attempt limit. Each attempt carries a
 * fresh Firebase token. Alerts raised while a socket was down are never
 * replayed (owner rule: no retrospective alerts mid-call).
 */

export const LIVE_RECONNECT_BASE_DELAY_MS = 1000;
export const LIVE_RECONNECT_MAX_DELAY_MS = 10_000;

/**
 * The reconnect overlay waits this long into a drop before it blocks the live
 * view, so a blip that recovers on the first attempt never flashes it. It
 * clears the moment every coaching socket is back.
 */
export const RECONNECT_OVERLAY_GRACE_MS = 2000;

/**
 * Every live socket that reports its reconnects to `connection.reconnectingSockets`.
 * `standalone` is a watcher's or co-seller's session socket on a desktop call
 * (`/ws/desktop`); `coSellerMic` is a co-seller's own mic on the transcription
 * proxy (D6, 2026-10-05).
 */
export const LIVE_SOCKET_NAMES = [
  'recall',
  'production',
  'landmine',
  'competitor',
  'calendar',
  'standalone',
  'coSellerMic',
] as const;

export type LiveSocketName = (typeof LIVE_SOCKET_NAMES)[number];

/**
 * The sockets that carry the live coaching (transcripts and alerts): a drop of
 * any of them blocks the live view with the reconnect overlay. The calendar
 * socket only announces bots, so its retries never block a call (the desktop
 * apps' overlay does not watch a calendar socket either).
 */
export const LIVE_COACHING_SOCKET_NAMES: readonly LiveSocketName[] = [
  'recall',
  'production',
  'landmine',
  'competitor',
  'standalone',
  // A co-seller whose mic is down is not heard: same overlay as the desktops' audio stream.
  'coSellerMic',
];

/** Same copy on macOS and WPF (REALTIME_ARCHITECTURE.md §10). */
export const LIVE_RECONNECT_COPY = {
  reconnecting: 'Reconnecting to live coaching…',
  reconnectingDetail: 'Transcripts and alerts resume as soon as the connection is back.',
  endSession: 'End session',
} as const;
